<script setup lang="ts">
import { computed, markRaw } from "vue";

import type { TableAltColumns, TableAltCustomElement, TableAltRows } from "./types";

import Loader from "../Loader.vue";

interface TableAltProps {
    columns?: TableAltColumns[];
    rows: TableAltRows[];

    showHeader?: boolean;
    showBottom?: boolean;
    globalActions?: {
        width?: string;
        // biome-ignore lint/suspicious/noExplicitAny: Init Biome
        headerActions?: TableAltCustomElement<any>[];
        // biome-ignore lint/suspicious/noExplicitAny: Init Biome
        rowActions?: TableAltCustomElement<any>[];
    };

    loading?: boolean;
    width?: string;
    gap?: number;
    rowHover?: boolean;
    rowClick?: boolean;
    leftPadding?: string;
    rightPadding?: string;
    cellLeftPadding?: string;
    cellRightPadding?: string;
    alwaysShowRowAction?: boolean;
    noHeaderBorderBottom?: boolean;
    rowSeparators?: boolean;
}

const emits = defineEmits<{
    (e: "rowClick", rowIndex: number): void;
    // biome-ignore lint/suspicious/noExplicitAny: Init Biome
    (event: "action", value: any): void;
    (e: "headerClick"): void;
}>();

const props = withDefaults(defineProps<TableAltProps>(), {
    showHeader: false,
    showBottom: false,
    rowSeparators: false,
});

const displayTableHeader = computed(() => {
    let columnsGenerator: string[] = [];
    if (props.columns) {
        columnsGenerator = props.columns.map((c) => c?.width ?? "1fr");
    }
    const { rowActions, headerActions, width } = props.globalActions ?? {};
    if (rowActions?.length || headerActions?.length) {
        columnsGenerator?.push(width ?? "1fr");
    }

    return {
        grid: columnsGenerator.join(" "),
    };
});

const handleComponentClick = (row: TableAltRows, cell: TableAltColumns, rowIndex: number) => {
    if (row.content[cell.field]?.customElement?.action) {
        // biome-ignore lint/style/noNonNullAssertion: Init Biome
        row.content[cell.field].customElement!.action!(rowIndex);
    }

    if (row.content[cell.field]?.customElement?.props.onClick) {
        row.content[cell.field].customElement?.props.onClick?.(rowIndex);
    }
};

const handleRowClick = (rowIndex: number) => {
    if (!props.rowClick) return;
    emits("rowClick", rowIndex);
};

// biome-ignore lint/suspicious/noExplicitAny: Init Biome
const handleRowActionComponentClick = (value: any) => {
    emits("action", value);
};

const handleHeaderClick = () => {
    emits("headerClick");
};

// biome-ignore lint/suspicious/noExplicitAny: Init Biome
const getComponent = (component: any) => {
    return typeof component === "object" ? markRaw(component) : component;
};
</script>

<template>
    <div class="ds-table-alt" :style="{ width: width ? width : '', gap: gap ? `${gap}px` : '' }">
        <div class="loader-container" v-if="loading">
            <Loader />
        </div>

        <template v-else>
            <!-- Header -->
            <div
                v-if="showHeader"
                :class="['table-header', { 'no-header-border-bottom': noHeaderBorderBottom }]"
                :style="{ 'grid-template-columns': displayTableHeader.grid, 'padding-left': leftPadding ?? '', 'padding-right': rightPadding ?? '' }"
                @click="handleHeaderClick"
            >
                <div
                    v-for="(cell, cellIindex) of columns"
                    :key="cellIindex"
                    class="header-cell"
                    :style="{ 'padding-left': cellLeftPadding ?? '', 'padding-right': cellRightPadding, ...cell.style }"
                >
                    <i v-if="cell.iconHeader" :class="`ti ti-${cell.iconHeader}`"></i>
                    <template v-if="cell.header">
                        <span
                            :class="[
                                'header-cell-text',
                                {
                                    leading: true,
                                },
                            ]"
                            :style="{ 'padding-left': cellLeftPadding ?? '', 'padding-right': cellRightPadding }"
                        >
                            {{ cell.header }}
                        </span>
                    </template>
                </div>
                <div v-if="!!globalActions?.headerActions" class="global-column-action" :style="{ width: globalActions.width ?? '' }">
                    <template v-for="(action, actionIndex) of globalActions.headerActions" :key="actionIndex">
                        <component v-if="action?.action" :is="getComponent(action.component)" v-bind="action.props" @click="action?.action?.()" />
                    </template>
                </div>
            </div>

            <!-- Rows -->
            <div class="rows-container" :class="{ 'with-separators': rowSeparators }" :style="{ gap: gap ? `${gap}px` : '', '--rows-gap': gap ? `${gap}px` : '' }">
                <div
                    v-for="(row, rowIndex) of rows"
                    :key="rowIndex"
                    class="table-row"
                    :class="[{ rowHover, rowClick, active: row.active }]"
                    :style="{
                        'grid-template-columns': displayTableHeader.grid,
                        'padding-left': leftPadding ?? '',
                        'padding-right': rightPadding ?? '',
                    }"
                    @click="handleRowClick(rowIndex)"
                >
                    <template v-if="!row?.editMode">
                        <!-- Cells -->
                        <div
                            v-for="(cell, cellIindex) of columns"
                            :key="cellIindex"
                            :class="{ 'table-cell': true, 'table-cell__hoverable': cell.displayOnHover }"
                            :style="{ 'padding-left': cellLeftPadding ?? '', 'padding-right': cellRightPadding, ...cell.style }"
                        >
                            <span
                                v-if="row.content[cell.field]?.text"
                                :class="[
                                    'cell-text',
                                    {
                                        leading: row.content[cell.field].text?.leading,
                                        passive: row.content[cell.field].text?.passive,
                                        none: row.content[cell.field].text?.none,
                                        empty: row.content[cell.field].text?.empty,
                                    },
                                    row.content[cell.field].text?.customClass,
                                ]"
                                :style="{ 'padding-left': cellLeftPadding ?? '', 'padding-right': cellRightPadding }"
                            >
                                {{ row.content[cell.field].text?.value }}
                            </span>
                            <component
                                v-else-if="row.content[cell.field]?.customElement"
                                :is="getComponent(row.content[cell.field].customElement?.component)"
                                @click="handleComponentClick(row, cell, rowIndex)"
                                @input="handleComponentClick(row, cell, rowIndex)"
                                v-bind="{ ...(row.content[cell.field].customElement?.props ?? {}), ...$attrs }"
                            />
                            <div v-else></div>
                        </div>
                        <!-- Global row action (at the end of the row) -->
                        <div
                            v-if="globalActions?.rowActions?.length"
                            :class="['global-row-action', { 'no-hide-row-action': alwaysShowRowAction }]"
                            :style="{ width: globalActions.width ?? '' }"
                        >
                            <template v-for="(action, actionIndex) of globalActions.rowActions" :key="actionIndex">
                                <div v-if="action?.action" @click.stop="action.action(rowIndex)">
                                    <component :is="getComponent(action.component)" v-bind="action.props" @action="handleRowActionComponentClick" />
                                </div>
                            </template>
                        </div>
                    </template>
                    <div v-else class="edit-slot">
                        <slot name="edit" :row="row" :rowIndex="rowIndex" />
                    </div>
                </div>
            </div>
        </template>
        <div v-if="$slots['bottom-slot']" class="table-row" :style="{ 'padding-left': leftPadding ?? '', 'padding-right': rightPadding ?? '' }">
            <div class="table-cell" :style="{ 'padding-left': cellLeftPadding ?? '', 'padding-right': cellRightPadding }">
                <slot name="bottom-slot" />
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.ds-table-alt {
    display: flex;
    flex-direction: column;
    gap: 4px;

    .loader-container {
        padding-left: 12px;
    }

    .table-header,
    .table-row {
        display: grid;
        align-items: center;
    }

    .header-cell-text,
    .cell-text {
        display: inline-block;
        width: 100%;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
    }

    .table-header {
        overflow: hidden;
        &:not(.no-header-border-bottom) {
            border-bottom: 0.5px solid $stroke-soft-200;
            margin-bottom: 4px;
            padding: 0 8px 2px 8px;
        }
        height: 30px;

        .header-cell {
            overflow: hidden;
            .header-cell-text {
                @include font-size-12;
                color: $text-sub-600;
                font-weight: 400;
                padding: 5px 4px;
                text-overflow: ellipsis;

                &.leading {
                    color: $text-main-900;
                    font-weight: 500;
                }
            }

            &.center {
                display: flex;
                justify-content: center;
            }
        }
    }

    .rows-container {
        display: flex;
        flex-direction: column;
        gap: 2px;

        &.with-separators {
            .table-row {
                position: relative;
            }

            .table-row:not(:last-child)::after {
                content: "";
                position: absolute;
                left: 8px;
                right: 8px;
                bottom: calc((var(--rows-gap, 1px) + 1px) / -2);
                height: 1px;
                background: $stroke-soft-200;
            }
        }
    }

    .table-cell {
        overflow: hidden;
        padding: 9px 4px;
        align-content: center;

        .cell-text {
            @include font-size-12;
            color: $text-main-900;
            font-weight: 400;

            &.leading {
                font-weight: 500;
            }
            &.passive {
                color: $text-sub-600;
            }
            &.none {
                color: $text-sub-600;
                font-weight: 500;
            }
            &.empty {
                color: $text-soft-400;
            }
        }

        &:has(.ds-button-alt) {
            padding: 0;
        }

        &__hoverable {
            display: none;
        }
    }

    .table-row {
        padding: 0 8px;
        height: 36px;
        border-radius: 4px;

        .global-row-action:not(.no-hide-row-action) {
            opacity: 0;
        }

        &.rowHover:hover:not(.table-header) {
            background-color: $neutral-50;
            border-radius: $radius-xs;
            .global-row-action {
                opacity: 1;
            }
        }

        &.rowClick {
            cursor: pointer;
        }

        &.active {
            background-color: $blue-100;
        }

        .edit-slot,
        .full-width {
            grid-column: 1 / -1;
        }
        &:has(.edit-slot) {
            height: fit-content;
            padding: 0;
            margin: 2px 0;
        }

        &:hover {
            .table-cell__hoverable {
                display: block;
            }
        }
    }

    .global-column-action,
    .global-row-action {
        padding: 0 4px;
        display: flex;
        justify-content: end;
        gap: 2px;
    }
}
</style>
